import { useState, useEffect, useRef } from "react";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const PERIODS = [
  { key: "today", label: "Bugün" },
  { key: "week", label: "Hafta" },
  { key: "month", label: "Ay" },
  { key: "single", label: "Tek Tarih" },
  { key: "custom", label: "Tarih Aralığı" },
];

const PriceChart = ({ product, label, onClose, profile, theme }) => {
  const [period, setPeriod] = useState("today");
  const [chartData, setChartData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  // Drag state
  const [dragPos, setDragPos] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const dragRef = useRef({ startX: 0, startY: 0, origX: 0, origY: 0 });
  const panelRef = useRef(null);

  const handleMouseDown = (e) => {
    if (e.target.closest('[data-testid="chart-close-btn"]')) return;
    e.preventDefault();
    const rect = panelRef.current.getBoundingClientRect();
    const startX = dragPos ? dragPos.x : rect.left;
    const startY = dragPos ? dragPos.y : rect.top;
    dragRef.current = { startX: e.clientX, startY: e.clientY, origX: startX, origY: startY };
    setIsDragging(true);
  };

  useEffect(() => {
    if (!isDragging) return;
    const handleMouseMove = (e) => {
      const dx = e.clientX - dragRef.current.startX;
      const dy = e.clientY - dragRef.current.startY;
      setDragPos({ x: dragRef.current.origX + dx, y: dragRef.current.origY + dy });
    };
    const handleMouseUp = () => setIsDragging(false);
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging]);

  const hasSatis = chartData?.has_satis !== false;
  const isMobile = window.innerWidth <= 768;
  const currency = product === "ons" || product === "usdkg" ? "$" : product === "eurkg" ? "€" : "₺";

  const isDark = theme === "lacivert" || theme === "yesil" || theme === "bordo";
  const colors = {
    bg: isDark ? "#0f172a" : "#ffffff",
    text: isDark ? "#f8fafc" : "#1f2937",
    subtext: isDark ? "#94a3b8" : "#6b7280",
    border: isDark ? "rgba(255,255,255,0.10)" : "rgba(0,0,0,0.08)",
    grid: isDark ? "rgba(255,255,255,0.07)" : "rgba(0,0,0,0.05)",
    elevated: isDark ? "rgba(255,255,255,0.04)" : "#FBFAF7",
    alis: "#3B9EFF",
    satis: "#E0A53E",
    up: "#22c55e",
    down: "#ef4444",
  };

  useEffect(() => {
    if (period === "custom" || period === "single") return;
    loadChart();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product, period]);

  const loadChart = async (customFrom, customTo) => {
    setLoading(true);
    try {
      let url = `${API}/history/chart?product=${product}&period=${period}&profile=${profile}`;
      if (period === "custom" && customFrom && customTo) {
        url += `&date_from=${customFrom}&date_to=${customTo}`;
      } else if (period === "single" && customFrom) {
        url += `&date_from=${customFrom}`;
      }
      const res = await axios.get(url);
      setChartData(res.data);
    } catch (e) {
      console.error("Chart error:", e);
      setChartData(null);
    } finally {
      setLoading(false);
    }
  };

  const handleCustomSearch = () => {
    if (period === "single" && dateFrom) {
      loadChart(dateFrom);
    } else if (period === "custom" && dateFrom && dateTo) {
      loadChart(dateFrom, dateTo);
    }
  };

  const formatPrice = (val) => {
    if (!val && val !== 0) return "0";
    return Number(val).toLocaleString("tr-TR", { minimumFractionDigits: 0, maximumFractionDigits: 2 });
  };

  return (
    <div data-testid="price-chart-overlay" onClick={dragPos ? undefined : onClose} style={{
      position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
      background: "rgba(0,0,0,0.7)", zIndex: 10000,
      display: "flex", justifyContent: "center", alignItems: "center",
      padding: "20px"
    }}>
      <div ref={panelRef} onClick={e => e.stopPropagation()} style={{
        background: colors.bg, borderRadius: "20px", width: "100%", maxWidth: "800px",
        maxHeight: "90vh", overflow: "auto", border: `1px solid ${colors.border}`,
        boxShadow: "0 24px 60px -12px rgba(0,0,0,0.55)",
        fontFamily: "'Outfit', system-ui, -apple-system, sans-serif",
        ...(dragPos ? { position: 'fixed', left: dragPos.x, top: dragPos.y, margin: 0, zIndex: 10001, transition: isDragging ? 'none' : undefined } : {})
      }}>
        {/* Header */}
        <div
          onMouseDown={handleMouseDown}
          data-testid="chart-header-drag"
          style={{
            display: "flex", justifyContent: "space-between", alignItems: "center",
            padding: "20px 24px 12px", borderBottom: `1px solid ${colors.border}`,
            cursor: isDragging ? 'grabbing' : 'grab', userSelect: 'none'
          }}
        >
          <h2 style={{ color: colors.text, fontSize: "20px", margin: 0, fontWeight: "bold" }}>{label}</h2>
          <button data-testid="chart-close-btn" onClick={onClose} style={{
            background: "none", border: "none", color: colors.subtext,
            fontSize: "28px", cursor: "pointer", lineHeight: 1
          }}>&times;</button>
        </div>

        {/* Current Price + Change + Opening */}
        {chartData && (
          <div style={{ display: "flex", gap: "24px", padding: "16px 24px", flexWrap: "wrap" }}>
            <div>
              <div style={{ color: colors.subtext, fontSize: "12px", marginBottom: "4px" }}>Alış Fiyatı</div>
              <div style={{ color: colors.alis, fontSize: "24px", fontWeight: "bold" }}>{formatPrice(chartData.current?.alis)}</div>
              <div style={{ fontSize: "14px", fontWeight: "bold", color: chartData.change?.alis >= 0 ? colors.up : colors.down, marginTop: "4px" }}>
                {chartData.change?.alis >= 0 ? "+" : ""}{formatPrice(chartData.change?.alis)}{currency} ({chartData.pct?.alis >= 0 ? "+" : ""}{chartData.pct?.alis}%)
              </div>
            </div>
            {hasSatis && chartData.current?.satis != null && (
              <div>
                <div style={{ color: colors.subtext, fontSize: "12px", marginBottom: "4px" }}>Satış Fiyatı</div>
                <div style={{ color: colors.satis, fontSize: "24px", fontWeight: "bold" }}>{formatPrice(chartData.current?.satis)}</div>
                <div style={{ fontSize: "14px", fontWeight: "bold", color: chartData.change?.satis >= 0 ? colors.up : colors.down, marginTop: "4px" }}>
                  {chartData.change?.satis >= 0 ? "+" : ""}{formatPrice(chartData.change?.satis)}{currency} ({chartData.pct?.satis >= 0 ? "+" : ""}{chartData.pct?.satis}%)
                </div>
              </div>
            )}
            {chartData.data?.length > 0 && (
              <div style={{ marginLeft: "auto", textAlign: "right" }}>
                <div style={{ color: colors.subtext, fontSize: "12px", marginBottom: "4px" }}>Açılış Fiyatı</div>
                {hasSatis && chartData.data[0]?.satis != null && (
                  <div style={{ color: colors.satis, fontSize: "16px", fontWeight: "bold" }}>{formatPrice(chartData.data[0].satis)}{currency}</div>
                )}
                <div style={{ color: colors.alis, fontSize: "16px", fontWeight: "bold" }}>{formatPrice(chartData.data[0].alis)}{currency}</div>
              </div>
            )}
          </div>
        )}

        {/* Period Tabs */}
        <div style={{ display: "flex", gap: "8px", padding: "0 24px 12px", flexWrap: "wrap" }}>
          {PERIODS.map(p => (
            <button key={p.key} onClick={() => setPeriod(p.key)} data-testid={`chart-period-${p.key}`} style={{
              padding: "8px 20px", borderRadius: "20px", cursor: "pointer", fontSize: "13px", fontWeight: "bold",
              background: period === p.key ? (isDark ? "#DA9F54" : "#333") : "transparent",
              color: period === p.key ? (isDark ? "#000" : "#fff") : colors.subtext,
              border: `1px solid ${period === p.key ? "transparent" : colors.border}`,
              transition: "all 0.2s"
            }}>
              {p.label}
            </button>
          ))}
        </div>

        {/* Single Date */}
        {period === "single" && (
          <div style={{ display: "flex", gap: "8px", padding: "0 24px 12px", alignItems: "center", flexWrap: "wrap" }}>
            <input
              type="date"
              value={dateFrom}
              onChange={e => setDateFrom(e.target.value)}
              data-testid="chart-single-date"
              style={{
                padding: "6px 10px", borderRadius: "8px", fontSize: "13px",
                border: `1px solid ${colors.border}`, background: isDark ? "#222" : "#f5f5f5",
                color: colors.text, outline: "none"
              }}
            />
            <button
              onClick={handleCustomSearch}
              data-testid="chart-single-search"
              style={{
                padding: "6px 16px", borderRadius: "8px", fontSize: "13px", fontWeight: "bold",
                background: isDark ? "#DA9F54" : "#333", color: isDark ? "#000" : "#fff",
                border: "none", cursor: "pointer"
              }}
            >
              Ara
            </button>
          </div>
        )}

        {/* Custom Date Range */}
        {period === "custom" && (
          <div style={{ display: "flex", gap: "8px", padding: "0 24px 12px", alignItems: "center", flexWrap: "wrap" }}>
            <input
              type="date"
              value={dateFrom}
              onChange={e => setDateFrom(e.target.value)}
              data-testid="chart-date-from"
              style={{
                padding: "6px 10px", borderRadius: "8px", fontSize: "13px",
                border: `1px solid ${colors.border}`, background: isDark ? "#222" : "#f5f5f5",
                color: colors.text, outline: "none"
              }}
            />
            <span style={{ color: colors.subtext, fontSize: "13px" }}>-</span>
            <input
              type="date"
              value={dateTo}
              onChange={e => setDateTo(e.target.value)}
              data-testid="chart-date-to"
              style={{
                padding: "6px 10px", borderRadius: "8px", fontSize: "13px",
                border: `1px solid ${colors.border}`, background: isDark ? "#222" : "#f5f5f5",
                color: colors.text, outline: "none"
              }}
            />
            <button
              onClick={handleCustomSearch}
              data-testid="chart-date-search"
              style={{
                padding: "6px 16px", borderRadius: "8px", fontSize: "13px", fontWeight: "bold",
                background: isDark ? "#DA9F54" : "#333", color: isDark ? "#000" : "#fff",
                border: "none", cursor: "pointer"
              }}
            >
              Ara
            </button>
          </div>
        )}

        {/* Chart */}
        <div style={{ padding: "0 12px 20px", height: "300px" }}>
          {loading && <div style={{ color: colors.subtext, textAlign: "center", paddingTop: "100px" }}>Yükleniyor...</div>}
          {!loading && chartData?.data?.length > 0 && (() => {
            const maxVal = Math.max(...chartData.data.map(d => Math.max(d.alis || 0, d.satis || 0)));
            const yWidth = maxVal >= 100000 ? 65 : maxVal >= 10000 ? 55 : 50;
            const displayData = isMobile && period !== "today"
              ? chartData.data.map(d => ({ ...d, time: d.time.split(" ")[0] }))
              : chartData.data;
            return (
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={displayData} margin={{ top: 10, right: 12, left: -8, bottom: 5 }}>
                <defs>
                  <linearGradient id="grad-alis" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={colors.alis} stopOpacity={0.30} />
                    <stop offset="100%" stopColor={colors.alis} stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="grad-satis" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={colors.satis} stopOpacity={0.30} />
                    <stop offset="100%" stopColor={colors.satis} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid vertical={false} stroke={colors.grid} strokeDasharray="3 3" />
                <XAxis dataKey="time" tick={{ fill: colors.subtext, fontSize: 10 }} tickLine={false}
                  axisLine={{ stroke: colors.grid }} minTickGap={24}
                  interval={Math.max(0, Math.floor(displayData.length / 8))} />
                <YAxis domain={["auto", "auto"]} tick={{ fill: colors.subtext, fontSize: 10 }} tickLine={false}
                  axisLine={false} tickFormatter={v => v.toLocaleString("tr-TR")} width={yWidth} />
                <Tooltip
                  contentStyle={{ background: isDark ? "rgba(15,23,42,0.96)" : "rgba(255,255,255,0.98)", border: `1px solid ${colors.border}`, borderRadius: "12px", fontSize: "13px", boxShadow: "0 8px 28px rgba(0,0,0,0.22)", padding: "10px 14px" }}
                  labelStyle={{ color: colors.text, fontWeight: "700", marginBottom: "6px" }}
                  cursor={{ stroke: colors.subtext, strokeWidth: 1, strokeDasharray: "4 4" }}
                  itemSorter={(item) => item.dataKey === "satis" ? -1 : 1}
                  formatter={(val, name) => {
                    const color = name === "alis" ? colors.alis : colors.satis;
                    const lbl = name === "alis" ? "Alış" : "Satış";
                    return [<span style={{ color, fontWeight: 700 }}>{formatPrice(val)}{currency}</span>, <span style={{ color }}>{lbl}</span>];
                  }}
                />
                {hasSatis && <Area type="monotone" dataKey="satis" stroke={colors.satis} strokeWidth={2.5} fill="url(#grad-satis)" name="satis" dot={false} activeDot={{ r: 4, strokeWidth: 0 }} />}
                <Area type="monotone" dataKey="alis" stroke={colors.alis} strokeWidth={2.5} fill="url(#grad-alis)" name="alis" dot={false} activeDot={{ r: 4, strokeWidth: 0 }} />
              </AreaChart>
            </ResponsiveContainer>
            );
          })()}
          {!loading && (!chartData || chartData?.data?.length === 0) && (
            <div style={{ color: colors.subtext, textAlign: "center", paddingTop: "100px" }}>
              {period === "custom" && !dateFrom ? "Tarih aralığı seçin" : period === "single" && !dateFrom ? "Tarih seçin" : "Grafik verisi bulunamadı"}
            </div>
          )}
        </div>

        {/* Stats */}
        {chartData?.data?.length > 0 && (() => {
          const alisValues = chartData.data.map(d => d.alis).filter(Boolean);
          const satisValues = chartData.data.map(d => d.satis).filter(Boolean);
          const boxStyle = { flex: 1, background: colors.elevated, borderRadius: "12px", padding: "12px 14px", border: `1px solid ${isDark ? "rgba(255,255,255,0.10)" : "rgba(218,159,84,0.35)"}` };
          const lblStyle = { fontSize: "11px", color: colors.subtext, display: "block", marginBottom: "2px" };
          const valStyle = { fontSize: "15px", fontWeight: "bold", color: colors.text, display: "block" };
          return (
            <div style={{ padding: "0 16px 12px", display: "flex", gap: "10px" }}>
              <div style={boxStyle}>
                <span style={lblStyle}>En Yüksek Alış</span>
                <span style={{ ...valStyle, color: colors.alis }}>{formatPrice(Math.max(...alisValues))}</span>
                <span style={{ ...lblStyle, marginTop: "8px" }}>En Düşük Alış</span>
                <span style={{ ...valStyle, color: colors.alis }}>{formatPrice(Math.min(...alisValues))}</span>
              </div>
              {hasSatis && satisValues.length > 0 && (
                <div style={boxStyle}>
                  <span style={lblStyle}>En Yüksek Satış</span>
                  <span style={{ ...valStyle, color: colors.satis }}>{formatPrice(Math.max(...satisValues))}</span>
                  <span style={{ ...lblStyle, marginTop: "8px" }}>En Düşük Satış</span>
                  <span style={{ ...valStyle, color: colors.satis }}>{formatPrice(Math.min(...satisValues))}</span>
                </div>
              )}
            </div>
          );
        })()}

        {/* Info */}
        {chartData && (
          <div style={{ padding: "0 24px 16px", color: colors.subtext, fontSize: "12px", textAlign: "center" }}>
            {chartData.count} veri noktası
          </div>
        )}
      </div>
    </div>
  );
};

export default PriceChart;
